const fold = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
const normalize = (value) => fold(value).trim();
const packages =
  "cajas?|cartones|carton|boxes|box|cases?|cartons?|bultos?|paquetes?|packs?|paq|cj|rollos?";
const pieces =
  "unidades|unidad|unds?|uds?|unids?|un|piezas?|pzas?|pcs?|pieces?|units?|etiquetas?|etq|bolsas?|fundas?|cajas?|rollos?|rolls?|kg";
const quantity = "(\\d{1,3}(?:[.,]\\d{3})+|\\d+)";
const count = `(${quantity.slice(1, -1)}|(?:\\d+\\s*)?mil)`;
const individualUnit = (value) =>
  /^(unidad|unidades|und|unds|ud|uds|unid|unids|pieza|piezas|pza|pzas|pc|pcs|piece|pieces|unit|units|each|etiqueta|etiquetas|bolsa|bolsas|funda|fundas)$/.test(
    normalize(value)
  );
const contentBase = (text) =>
  /^(rollos?|rolls?)$/.test(normalize(text))
    ? "rollo"
    : normalize(text) === "kg"
    ? "kg"
    : "unidad";
const compatibleBase = (a, b) =>
  normalize(a) === normalize(b) ||
  (individualUnit(a) && individualUnit(b)) ||
  (/^(rollos?|rolls?)$/.test(normalize(a)) && b === "rollo");
const packageUnit = (text) => {
  const value = normalize(text);
  if (/^(cajas?|boxes|box|cases?|cj)$/.test(value)) return "caja";
  if (/^(cartones|carton|cartons?)$/.test(value)) return "cartón";
  if (/^bultos?$/.test(value)) return "bulto";
  if (/^rollos?$/.test(value)) return "rollo";
  return "paquete";
};
const amountOf = (text) => {
  const value = normalize(text),
    amount = value.endsWith("mil")
      ? Number(value.slice(0, -3).trim() || 1) * 1000
      : Number(value.replace(/[.,]/g, ""));
  return Number.isSafeInteger(amount) && amount > 0 && amount <= 1e9
    ? String(amount)
    : null;
};
const sourcesOf = (product) => [
  ...new Set(
    [
      product.name,
      product.specification,
      product.qbSource?.purchaseDescription,
      product.qbSource?.salesDescription,
    ].filter(Boolean)
  ),
];

function reviewCandidates(product, candidates, nested = false) {
  const bases = new Set(candidates.map((c) => c.baseUnit));
  const units = new Map();
  let conflicting = false;
  for (const c of candidates) {
    if (units.has(c.unit) && units.get(c.unit) !== c.factorToBase)
      conflicting = true;
    units.set(c.unit, c.factorToBase);
  }
  if (nested || conflicting || bases.size > 1)
    return {
      status: "ambiguous",
      candidates,
      reason: nested ? "nested" : "conflicting",
    };
  const baseUnit = product.baseUnit || candidates[0].baseUnit;
  const result = { ...candidates[0], candidates, baseUnit };
  if (
    !compatibleBase(baseUnit, candidates[0].baseUnit) ||
    candidates.some((c) => normalize(c.unit) === normalize(baseUnit))
  )
    return { ...result, status: "incompatible" };
  const existing = product.conversions || [];
  if (
    candidates.some((c) =>
      existing.some(
        (e) =>
          normalize(e.unit) === normalize(c.unit) &&
          Number(e.factorToBase) !== Number(c.factorToBase)
      )
    )
  )
    return { ...result, status: "conflict" };
  return {
    ...result,
    status: candidates.every((c) =>
      existing.some((e) => normalize(e.unit) === normalize(c.unit))
    )
      ? "present"
      : "suggested",
  };
}

// Descriptions suggest reviewed conversions; they never confirm invoice quantity units.
export function packagingSuggestion(product) {
  const candidates = new Map(),
    quantities = new Map(),
    prices = new Map();
  let nested = false;
  const patterns = [
    // Explicit content: caja de 1000 unidades; cada paquete son 25 unds; CJ / 6 rollos.
    [
      new RegExp(
        `\\b(${packages})\\b\\s*(?:de|of|con|x|:|/|son|es)?\\s*[([]?\\s*${count}\\s*(${pieces})\\b`,
        "gi"
      ),
      1,
      2,
      3,
    ],
    // Product boxes are pieces inside a package, rather than the enclosing package.
    [
      new RegExp(
        `\\b${count}\\s*(${pieces})\\s*(?:por|per|/|x)\\s*(${packages})\\b`,
        "gi"
      ),
      3,
      1,
      2,
    ],
    // The package is explicit even without the word unidades: 1000 X CAJA.
    [
      new RegExp(
        `\\b${count}\\s*(?:por|per|/|x)\\s*(cajas?|paq|paquetes?)\\b`,
        "gi"
      ),
      2,
      1,
      null,
    ],
    [new RegExp(`(cj)\\s*/\\s*${count}\\s*(rollos?)\\b`, "gi"), 1, 2, 3],
    // Commercial quantity can differ from the physical case: unidad=1000, caja=8000.
    [
      new RegExp(
        `\\bcada\\s+(?:unidad|und|unid)\\s+(?:son|es|con)\\s+${count}\\s*(${pieces}|cajas?)\\b`,
        "gi"
      ),
      null,
      1,
      2,
    ],
  ];
  const nestedPattern = new RegExp(
    `\\b(?:${packages})\\s*(?:de|of|con|x)?\\s*${quantity}\\s*(?:paquetes?|packs?|bultos?)\\b`,
    "i"
  );
  for (const source of sourcesOf(product)) {
    const text = fold(source);
    nested ||= nestedPattern.test(text);
    for (const [pattern, unitIndex, countIndex, baseIndex] of patterns) {
      for (const match of text.matchAll(pattern)) {
        if (
          /^\d/.test(match[0]) &&
          /[\d.,+-]/.test(text[match.index - 1] || "")
        )
          continue;
        const factorToBase = amountOf(match[countIndex]);
        if (!factorToBase) continue;
        const unit = unitIndex
          ? packageUnit(match[unitIndex])
          : "unidad comercial";
        const baseUnit = baseIndex ? contentBase(match[baseIndex]) : "unidad";
        const evidence = source.slice(
          match.index,
          match.index + match[0].length
        );
        candidates.set(`${unit}:${baseUnit}:${factorToBase}`, {
          unit,
          factorToBase,
          baseUnit,
          evidence,
        });
      }
    }
    // Price bases are evidence only, never a physical packing suggestion.
    for (const match of text.matchAll(
      new RegExp(`\\bprecio\\s+por\\s+${count}\\s*(${pieces})\\b`, "gi")
    )) {
      const factorToBase = amountOf(match[1]);
      if (factorToBase)
        prices.set(factorToBase, {
          factorToBase,
          baseUnit: contentBase(match[2]),
          evidence: source.slice(match.index, match.index + match[0].length),
        });
    }
    // Parentheses have priority over capacity descriptions such as 1 galón / 4 unid (25 und).
    const quantityUnits = pieces.replace("|kg", "");
    const parenthesized = [
      ...text.matchAll(
        new RegExp(`\\(\\s*${count}\\s*(${quantityUnits})\\s*\\)`, "gi")
      ),
    ];
    const bare = parenthesized.length
      ? parenthesized
      : [
          ...text.matchAll(
            new RegExp(
              `(?:\\b|["']x)${count}\\s*(${quantityUnits})\\b(?=\\s*[.,]*\\s*$)`,
              "gi"
            )
          ),
        ];
    for (const match of bare) {
      if (/^\d/.test(match[0]) && /[\d.,+-]/.test(text[match.index - 1] || ""))
        continue;
      const factorToBase = amountOf(match[1]);
      if (/precio\s+por\s*$/.test(text.slice(0, match.index))) continue;
      if (factorToBase)
        quantities.set(contentBase(match[2]) + ":" + factorToBase, {
          factorToBase,
          baseUnit: contentBase(match[2]),
          evidence: source.slice(match.index, match.index + match[0].length),
        });
    }
  }
  if (candidates.size) {
    const found = [...candidates.values()];
    const unmatched = [...quantities.values()].filter(
      (q) =>
        !found.some(
          (c) => c.baseUnit === q.baseUnit && c.factorToBase === q.factorToBase
        )
    );
    if (unmatched.length)
      return {
        status: "ambiguous",
        candidates: [...found, ...unmatched],
        reason: "conflicting",
      };
    return reviewCandidates(product, found, nested);
  }
  const evidence = prices.size
    ? [...prices.values()]
    : [...quantities.values()];
  if (!evidence.length) return null;
  if (evidence.length > 1)
    return { status: "ambiguous", candidates: evidence, reason: "conflicting" };
  return {
    ...evidence[0],
    status: prices.size ? "price_basis" : "quantity_only",
  };
}

export function applyPackagingSuggestion(product, suggestion) {
  if (suggestion?.status !== "suggested") return product;
  const candidates = suggestion.candidates || [suggestion];
  const existing = product.conversions || [];
  return {
    ...product,
    baseUnit: product.baseUnit || suggestion.baseUnit,
    conversions: [
      ...existing,
      ...candidates
        .filter(
          (c) => !existing.some((e) => normalize(e.unit) === normalize(c.unit))
        )
        .map((c) => ({
          unit: c.unit,
          factorToBase: c.factorToBase,
          label: "Descripción: " + c.evidence.slice(0, 250),
        })),
    ],
  };
}

export function defineQuantityPackaging(product, suggestion, unit) {
  if (
    suggestion?.status !== "quantity_only" ||
    ![
      "caja",
      "cartón",
      "bulto",
      "paquete",
      "rollo",
      "unidad comercial",
    ].includes(unit)
  )
    return product;
  return applyPackagingSuggestion(
    product,
    reviewCandidates(product, [{ ...suggestion, unit }])
  );
}

// Send editable fields only, and compare semantics rather than audit dates/subdocument IDs.
export function productValues(product) {
  return {
    businessUnit: product.businessUnit,
    code: product.code,
    name: product.name,
    specification: product.specification || "",
    category: product.category || "",
    baseUnit: product.baseUnit || "",
    conversions: (product.conversions || []).map((c) => ({
      unit: c.unit,
      factorToBase: String(c.factorToBase),
      label: c.label || "",
    })),
    weightKgPerBase: product.weightKgPerBase ?? null,
    volumeM3PerBase: product.volumeM3PerBase ?? null,
    hsCode: product.hsCode || "",
    dutyRule: product.dutyRule || null,
    requiredDocTypes: product.requiredDocTypes || [],
    status: product.status || "active",
    qbLinks: (product.qbLinks || []).map((l) => ({
      realmId: l.realmId,
      quickBooksId: l.quickBooksId,
    })),
    supplyItem: product.supplyItem || null,
    sparePart: product.sparePart || null,
  };
}
